<template>
  <section class="bg-white py-16 border-t border-gray-200">
    <div class="container mx-auto grid gap-10 px-4 lg:grid-cols-12 lg:items-center">
      <div class="lg:col-span-6" v-scroll-reveal="{ delay: 0.1 }">
        <p class="text-sm uppercase tracking-[0.6em] text-amber-500">Any Country • Low Fare</p>
        <h2 class="mt-4 text-4xl font-bold text-gray-900 lg:text-5xl">
          Secure Your Flights & Visa in a Single Request
        </h2>
        <p class="mt-4 text-gray-600 leading-relaxed">
          Our aviation desk accesses unpublished Umrah fares from over 40 airlines. We coordinate your visa approval and flight itinerary together, ensuring you arrive on preferred dates with comfortable layovers.
        </p>
        <ul class="mt-6 space-y-3 text-gray-700">
          <li class="flex items-center gap-3">
            <span class="h-1 w-6 rounded-full bg-amber-400" />
            Flexible departures from any major city worldwide.
          </li>
          <li class="flex items-center gap-3">
            <span class="h-1 w-6 rounded-full bg-amber-400" />
            Instant visa tracking and document verification.
          </li>
          <li class="flex items-center gap-3">
            <span class="h-1 w-6 rounded-full bg-amber-400" />
            Dedicated concierge for reschedules and special meals.
          </li>
        </ul>
      </div>

      <div class="lg:col-span-6" v-scroll-reveal="{ delay: 0.2 }">
        <div class="rounded-[32px] border border-gray-100 bg-white p-8 shadow-xl">
          <h3 class="text-2xl font-semibold text-gray-900">Request Low Fare Options</h3>
          <p class="mt-2 text-sm text-gray-500">We reply within 30 minutes during working hours.</p>
          <form @submit.prevent="handleSubmit" class="mt-6 space-y-4">
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="text-xs uppercase tracking-wide text-gray-500">Departure City</label>
                <input
                  v-model="formData.departureCity"
                  type="text"
                  placeholder="e.g. Lahore, PK"
                  :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-gray-800 focus:outline-none', errors.departureCity ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-400']"
                  @blur="validateField('departureCity')"
                />
                <p v-if="errors.departureCity" class="mt-1 text-xs text-red-500">{{ errors.departureCity }}</p>
              </div>
              <div>
                <label class="text-xs uppercase tracking-wide text-gray-500">Destination</label>
                <input
                  v-model="formData.destination"
                  type="text"
                  placeholder="Jeddah or Madina"
                  :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-gray-800 focus:outline-none', errors.destination ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-400']"
                  @blur="validateField('destination')"
                />
                <p v-if="errors.destination" class="mt-1 text-xs text-red-500">{{ errors.destination }}</p>
              </div>
            </div>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="text-xs uppercase tracking-wide text-gray-500">Departure Date</label>
                <input
                  v-model="formData.departureDate"
                  type="date"
                  :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-gray-800 focus:outline-none', errors.departureDate ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-400']"
                  @blur="validateField('departureDate')"
                />
                <p v-if="errors.departureDate" class="mt-1 text-xs text-red-500">{{ errors.departureDate }}</p>
              </div>
              <div>
                <label class="text-xs uppercase tracking-wide text-gray-500">Return Date</label>
                <input
                  v-model="formData.returnDate"
                  type="date"
                  :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-gray-800 focus:outline-none', errors.returnDate ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-400']"
                  @blur="validateField('returnDate')"
                />
                <p v-if="errors.returnDate" class="mt-1 text-xs text-red-500">{{ errors.returnDate }}</p>
              </div>
            </div>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="text-xs uppercase tracking-wide text-gray-500">Passengers</label>
                <select
                  v-model="formData.passengers"
                  :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-gray-800 focus:outline-none', errors.passengers ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-400']"
                  @blur="validateField('passengers')"
                >
                  <option value="">Select Passengers</option>
                  <option>1 Adult</option>
                  <option>2 Adults</option>
                  <option>Family (4+)</option>
                  <option>Group (10+)</option>
                </select>
                <p v-if="errors.passengers" class="mt-1 text-xs text-red-500">{{ errors.passengers }}</p>
              </div>
              <div>
                <label class="text-xs uppercase tracking-wide text-gray-500">Cabin</label>
                <select
                  v-model="formData.cabin"
                  :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-gray-800 focus:outline-none', errors.cabin ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-400']"
                  @blur="validateField('cabin')"
                >
                  <option value="">Select Cabin</option>
                  <option>Economy</option>
                  <option>Premium Economy</option>
                  <option>Business</option>
                  <option>First Class</option>
                </select>
                <p v-if="errors.cabin" class="mt-1 text-xs text-red-500">{{ errors.cabin }}</p>
              </div>
            </div>
            <button
              type="submit"
              class="mt-4 w-full rounded-2xl bg-amber-500 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-amber-600"
            >
              Request Options
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FlightDealsSection',
  data() {
    return {
      whatsappNumber: '923185073318',
      formData: {
        departureCity: '',
        destination: '',
        departureDate: '',
        returnDate: '',
        passengers: '',
        cabin: ''
      },
      errors: {
        departureCity: '',
        destination: '',
        departureDate: '',
        returnDate: '',
        passengers: '',
        cabin: ''
      }
    }
  },
  methods: {
    validateField(fieldName) {
      this.errors[fieldName] = ''
      
      switch (fieldName) {
        case 'departureCity':
          if (!this.formData.departureCity || this.formData.departureCity.trim() === '') {
            this.errors.departureCity = 'Departure city is required'
          }
          break
        case 'destination':
          if (!this.formData.destination || this.formData.destination.trim() === '') {
            this.errors.destination = 'Destination is required'
          }
          break
        case 'departureDate':
          if (!this.formData.departureDate || this.formData.departureDate.trim() === '') {
            this.errors.departureDate = 'Departure date is required'
          }
          break
        case 'returnDate':
          if (!this.formData.returnDate || this.formData.returnDate.trim() === '') {
            this.errors.returnDate = 'Return date is required'
          } else if (this.formData.departureDate && this.formData.returnDate && new Date(this.formData.returnDate) < new Date(this.formData.departureDate)) {
            this.errors.returnDate = 'Return date must be after departure date'
          }
          break
        case 'passengers':
          if (!this.formData.passengers || this.formData.passengers.trim() === '') {
            this.errors.passengers = 'Number of passengers is required'
          }
          break
        case 'cabin':
          if (!this.formData.cabin || this.formData.cabin.trim() === '') {
            this.errors.cabin = 'Cabin class is required'
          }
          break
      }
    },
    validateForm() {
      // Validate all fields
      this.validateField('departureCity')
      this.validateField('destination')
      this.validateField('departureDate')
      this.validateField('returnDate')
      this.validateField('passengers')
      this.validateField('cabin')
      
      // Check if there are any errors
      return !Object.values(this.errors).some(error => error !== '')
    },
    handleSubmit() {
      // Validate form before submission
      if (!this.validateForm()) {
        return
      }

      // Build WhatsApp message with form data
      const message = `*Flight Booking Request*\n\n` +
        `*Departure City:* ${this.formData.departureCity}\n` +
        `*Destination:* ${this.formData.destination}\n` +
        `*Departure Date:* ${this.formData.departureDate}\n` +
        `*Return Date:* ${this.formData.returnDate}\n` +
        `*Passengers:* ${this.formData.passengers}\n` +
        `*Cabin:* ${this.formData.cabin}`

      // Encode the message
      const encodedMessage = encodeURIComponent(message)

      // Clean the WhatsApp number
      const cleanNumber = this.whatsappNumber.replace(/[^0-9]/g, '')

      // Build WhatsApp URL
      const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodedMessage}`

      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank')

      // Reset form after submission
      this.formData = {
        departureCity: '',
        destination: '',
        departureDate: '',
        returnDate: '',
        passengers: '',
        cabin: ''
      }
      this.errors = {
        departureCity: '',
        destination: '',
        departureDate: '',
        returnDate: '',
        passengers: '',
        cabin: ''
      }
    }
  }
}
</script>

